import styled from '@emotion/styled';
import { Alert, Stack, Text, Button, Tooltip, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { useCurrentSite } from '@hotjar/site';
import { useAddToDemoSite } from '@hotjar/webapp/domains/activation/product-tour';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import {
  FEEDBACK as FEEDBACK_ROUTE,
  HEATMAPS as HEATMAPS_ROUTE,
  HIGHLIGHTS as HIGHLIGHTS_ROUTE,
  PLAYBACKS as PLAYBACKS_ROUTE,
  SURVEYS as SURVEYS_ROUTE,
} from '@hotjar/webapp/constants/routes';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import FeedbackImage from './assets/feedback.jpg';
import RecordingsImage from './assets/recordings.jpg';
import HeatmapsImage from './assets/heatmaps.jpg';
import HighlightsImage from './assets/highlights.jpg';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x2};
  justify-content: space-between;
  width: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${tokens.spacer.x2} 0;
  width: calc(50% - ${tokens.spacer.x1});
`;

const StyledAlert = styled(Alert)`
  width: 100%;
  max-width: max-content;
  padding: 6px 50px 6px ${tokens.spacer.x1pt5};
  margin-bottom: ${tokens.spacer.x2};
  height: 28px;
  border-radius: 0;
  min-height: unset;
  align-items: center;
  align-self: flex-start;

  * {
    margin: 0;
    flex-grow: 0;
    flex-shrink: 1;
  }

  svg {
    margin: 0;
  }

  p {
    margin: 0;
    color: ${tokens.color.text_light};
  }
`;

const EmptyStateImage = styled.div<{ background?: string }>`
  display: flex;
  background: url('${({ background }) => background}');
  width: calc(50% - ${tokens.spacer.x1});
  width: calc(50% - 8px);
  max-width: 372px;
  background-position: top center;
  background-size: cover;
`;

const StyledHeading = styled(Text)`
  margin-bottom: ${tokens.spacer.x1};
`;

const StyledText = styled(Text)`
  margin-bottom: ${tokens.spacer.x1pt5};
  max-width: 432px;
`;

export enum EmptyStateKind {
  RECORDINGS = 'recordings',
  HEATMAPS = 'heatmaps',
  HIGHLIGHTS = 'highlights',
  FEEDBACK = 'feedback',
  SURVEYS = 'surveys',
}

export type FeatureEmptyStateProps = {
  kind: EmptyStateKind;
};

const EMPTY_STATE_MAP: {
  [key in EmptyStateKind]: {
    title: string;
    text: string;
    image?: string;
    demoLink: string;
    demoOptions?: Record<string, string | number>;
  };
} = {
  [EmptyStateKind.RECORDINGS]: {
    title: 'recordings.title',
    text: 'recordings.text',
    image: RecordingsImage,
    demoLink: PLAYBACKS_ROUTE.LIST.path,
  },
  [EmptyStateKind.HEATMAPS]: {
    title: 'heatmaps.title',
    text: 'heatmaps.text',
    image: HeatmapsImage,
    demoLink: HEATMAPS_ROUTE.LIST_CONTINUOUS_CAPTURE.path,
  },
  [EmptyStateKind.HIGHLIGHTS]: {
    title: 'highlights.title',
    text: 'highlights.text',
    image: HighlightsImage,
    demoLink: HIGHLIGHTS_ROUTE.LIST.path,
    demoOptions: { collectionId: '0' },
  },
  [EmptyStateKind.FEEDBACK]: {
    title: 'feedback.title',
    text: 'feedback.text',
    image: FeedbackImage,
    demoLink: FEEDBACK_ROUTE.LIST.path,
  },
  [EmptyStateKind.SURVEYS]: {
    title: 'surveys.title',
    text: 'surveys.text',
    demoLink: SURVEYS_ROUTE.LIST.path,
  },
};

export const FeatureEmptyState = ({ kind }: FeatureEmptyStateProps): JSX.Element => {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const { installed: isTCInstalled } = useTrackingCodeState();
  const { show: showTrackingCodeModal } = useModalTrackingCode();
  const { click: trackClick } = useTrack();
  const { isLoading: isAddToDemoSiteLoading, addToDemoSite } = useAddToDemoSite();
  const hasFeedbackCreatePermission = usePermission('feedbacks-create');
  const UI_POSITION = isTCInstalled ? 'Empty-states post-setup' : 'Empty-states pre-setup';

  const handleTrackingCodeCTAClick = () => {
    if (currentSite) {
      showTrackingCodeModal(currentSite, 2, UI_POSITION);
      trackClick(`${kind} Install`, UI_POSITION, {});
    }
  };

  const handleDemoSiteCTAClick = () => {
    trackClick(`${kind} Product-tour`, UI_POSITION, {});
    addToDemoSite(EMPTY_STATE_MAP[kind].demoLink, EMPTY_STATE_MAP[kind].demoOptions);
  };

  const handleFeedbackCreate = () => {
    if (currentSite?.id) {
      trackClick(`Create Feedback`, UI_POSITION, {});
      navigateTo(FEEDBACK_ROUTE.CREATE.path, { siteId: currentSite?.id });
    }
  };

  return (
    <Wrapper>
      <ContentWrapper>
        <StyledHeading textType="header2" as="h2">
          {t(`activation:feature-empty-state.${EMPTY_STATE_MAP[kind].title}`)}
        </StyledHeading>
        <StyledText textType="body1">
          {t(`activation:feature-empty-state.${EMPTY_STATE_MAP[kind].text}`)}
        </StyledText>
        {!isTCInstalled ? (
          <StyledAlert kind="warning">{t('activation:feature-empty-state.tc-alert')}</StyledAlert>
        ) : null}
        <Stack direction="row">
          {!isTCInstalled ? (
            <Button onClick={handleTrackingCodeCTAClick} size="s" kind="primary">
              {t('activation:space-overview.tracking-code.install-hotjar')}
            </Button>
          ) : (
            <>
              {kind === EmptyStateKind.FEEDBACK ? (
                <Tooltip
                  text={t('activation:empty-states.pending-setup.feedback.lacking-permissions')}
                  isDisabled={hasFeedbackCreatePermission}
                >
                  <span>
                    <Button
                      kind="primary"
                      size="s"
                      onClick={handleFeedbackCreate}
                      icon="plus"
                      isDisabled={!hasFeedbackCreatePermission}
                    >
                      {t('activation:empty-states.pending-setup.feedback.cta')}
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </>
          )}
          <Button
            isLoading={isAddToDemoSiteLoading}
            loadingText={t(`activation:space-overview.product-tour.loading-demo-site`)}
            onClick={handleDemoSiteCTAClick}
            size="s"
            kind="secondary"
          >
            {t('activation:feature-empty-state.demo-site-cta', {
              feature: t(`activation:feature-empty-state.${EMPTY_STATE_MAP[kind].title}`),
            })}
          </Button>
        </Stack>
      </ContentWrapper>
      {EMPTY_STATE_MAP[kind].image ? (
        <EmptyStateImage
          data-testid="empty-state-image"
          background={EMPTY_STATE_MAP[kind].image}
          aria-hidden="true"
        />
      ) : null}
    </Wrapper>
  );
};
